/**
 * Historical BOM search: one search box (multi-word AND, 160 ms debounce), category / customer / complete / picture
 * filters, picture grid in pages of 120, detail overlay. Search text, filters, number shown and scroll position are
 * remembered (ic2_bomq / ic2_bomscroll) across tab switches and reloads.
 * The same browser is shown in the left half of the cost sheet for "Compare with history" (JF 2026-09-30): identical
 * search, pictures and detail view, with its own search and the detail opened inside the panel.
 */
import { useEffect, useRef, useState } from "react";
import { cleanDesc, cleanPos, hasSheet } from "@ic/engine";
import { scrollPos, useStore } from "../../store.ts";
import { useT } from "../../shared/i18n.ts";
import { useMoney } from "../../shared/money.ts";
import { api, type Card } from "../../shared/api.ts";
import { KEYS, lsGet, lsSet } from "../../shared/storage.ts";
import { applyBomTo } from "./ApplyDialog.tsx";

const PAGE = 120;
interface BQ { q: string; cat: string; cu: string; ok: boolean; img: boolean; shown: number }
const bigImg = (u: string) => String(u || "").replace(/resize,w_\d+/, "resize,w_900");

export function BomTab({ active }: { active: boolean }) {
  return <BomBrowser active={active} mode="tab" />;
}

/** `tab` = the 历史 BOM tab (search remembered, page scroll); `compare` = inside the comparison panel (own search). */
export function BomBrowser({ active, mode }: { active: boolean; mode: "tab" | "compare" }) {
  const t = useT();
  const meta = useStore((s) => s.meta);
  const tab = mode === "tab";
  const [bq, setBq] = useState<BQ>(() => {
    const base: BQ = { q: "", cat: "", cu: "", ok: true, img: false, shown: PAGE };
    if (tab) return { ...base, ...lsGet<Partial<BQ>>(KEYS.bomq, {}) };
    const sub = useStore.getState().sheet.ai?.sub; // compare: start with the sheet's sub-category
    return { ...base, cat: sub ? "s:" + sub : "" };
  });
  const [res, setRes] = useState<{ total: number; items: Card[] } | null>(null);
  // the detail shown: in the tab a local overlay; in the comparison the chosen SC lives in the store
  // (so "Compare with the current cost sheet" in the tab can open it here)
  const [tabDetail, setTabDetail] = useState<string | null>(null);
  const cmpSc = useStore((s) => s.compare.sc);
  const detail = tab ? tabDetail : cmpSc;
  const setDetail = (sc: string | null) => (tab ? setTabDetail(sc) : useStore.getState().setCompare({ on: true, sc }));
  const restored = useRef(false);
  // comparison panel: it scrolls by itself — a detail opens at the top, "back" returns to where the pictures were
  const root = useRef<HTMLDivElement>(null);
  const gridScroll = useRef(0);
  useEffect(() => {
    if (tab) return;
    const panel = root.current?.closest(".cmp");
    if (!panel) return;
    if (detail) panel.scrollTop = 0;
    else requestAnimationFrame(() => { panel.scrollTop = gridScroll.current; });
  }, [detail, tab]);
  const seq = useRef(0);

  // fetch whenever the query changes (text and customer debounced); "show more" only fetches the next page
  useEffect(() => {
    if (!active && !res) return;
    const id = ++seq.current;
    const tm = setTimeout(async () => {
      try {
        // restore up to the remembered number shown (the API returns at most 500 per call)
        const q = { q: bq.q, cat: bq.cat, cu: bq.cu, ok: bq.ok, img: bq.img };
        const r = await api.search({ ...q, offset: 0, limit: Math.min(bq.shown, 500) });
        while (r.items.length < Math.min(bq.shown, r.total)) {
          const n = await api.search({ ...q, offset: r.items.length, limit: Math.min(bq.shown - r.items.length, 500) });
          if (!n.items.length) break;
          r.items.push(...n.items);
        }
        if (id !== seq.current) return;
        setRes(r);
        if (tab && !restored.current) { restored.current = true; requestAnimationFrame(() => window.scrollTo(0, scrollPos.bom || 0)); }
      } catch { /* toast shown by App when the API is down */ }
    }, res ? 160 : 0);
    return () => clearTimeout(tm);
  }, [bq.q, bq.cat, bq.cu, bq.ok, bq.img, active]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (tab) lsSet(KEYS.bomq, bq); }, [bq, tab]);
  useEffect(() => {
    if (!active || !tab) return;
    const s = () => { scrollPos.bom = window.scrollY; lsSet(KEYS.bomScroll, window.scrollY); };
    window.addEventListener("scroll", s, { passive: true });
    return () => window.removeEventListener("scroll", s);
  }, [active, tab]);

  const change = (p: Partial<BQ>) => setBq((b) => ({ ...b, ...p, shown: PAGE }));
  async function more() {
    if (!res) return;
    const r = await api.search({ q: bq.q, cat: bq.cat, cu: bq.cu, ok: bq.ok, img: bq.img, offset: res.items.length, limit: PAGE });
    setRes({ total: r.total, items: [...res.items, ...r.items] });
    setBq((b) => ({ ...b, shown: b.shown + PAGE }));
  }

  const cats = meta?.categories || {};
  const tot = res?.total ?? 0, sh = res?.items.length ?? 0;
  return (
    <div ref={root}>
      <div className="bsearch">
        <input className="in big" id={tab ? "bq" : "cmpQ"} autoComplete="off" value={bq.q} onChange={(e) => change({ q: e.target.value })}
          placeholder={t("Search style no. / SC / customer / category / description …  (results update as you type)", "搜索款号 / SC / 客户 / 品类 / 描述……（边打字边出结果）")} />
        <select className="in" id="bcat" value={bq.cat} onChange={(e) => change({ cat: e.target.value })}>
          <option value="">{t("All categories", "全部品类")}</option>
          {Object.keys(cats).map((c) => (
            <optgroup label={c} key={c}>
              <option value={"c:" + c}>{c} — {t("all", "全部")}</option>
              {cats[c].map((s) => <option value={"s:" + s} key={s}>{s}</option>)}
            </optgroup>
          ))}
        </select>
        <input className="in cu" id="bcu" list="custList" value={bq.cu} onChange={(e) => change({ cu: e.target.value })} placeholder={t("Customer", "客户")} />
        <datalist id="custList">{(meta?.customers || []).map((c) => <option value={c} key={c} />)}</datalist>
        <label className="ck2"><input type="checkbox" id="bok" checked={bq.ok} onChange={(e) => change({ ok: e.target.checked })} /><span>{t("Styles with BOM lines", "只看有 BOM 的款")}</span></label>
        <label className="ck2"><input type="checkbox" id="bimg" checked={bq.img} onChange={(e) => change({ img: e.target.checked })} /><span>{t("With picture only", "只看有图")}</span></label>
      </div>
      {!tab && detail && <Detail sc={detail} inline onClose={() => setDetail(null)} />}
      <div style={!tab && detail ? { display: "none" } : undefined}>
      <div className="bcount" id={tab ? "bcount" : "cmpCount"}>
        {res && (tot ? <>{tot.toLocaleString()} {t("styles", "个款")}{sh < tot ? ` · ${t("showing", "显示")} ${sh}` : ""} · <span className="dim">{t("click a picture for details", "点图片看详情")}</span></>
          : t("No styles match — try fewer words or untick a filter.", "没有符合的款——少打几个词或取消勾选试试。"))}
      </div>
      <div className="grid" id={tab ? "bgrid" : "cmpGrid"}>{res?.items.map((x, index) => <CardView x={x} index={index} key={x.sc} onOpen={() => {
        // remember where the pictures were now: once the detail replaces them the panel is already back at the top
        if (!tab) gridScroll.current = root.current?.closest(".cmp")?.scrollTop ?? 0;
        setDetail(x.sc);
      }} />)}</div>
      <div className="more" id={tab ? "bmore" : undefined}>{sh < tot && <button className="btn" id={tab ? "bMoreBtn" : undefined} onClick={more}>{t("Show more", "显示更多")} ({(tot - sh).toLocaleString()})</button>}</div>
      </div>
      {tab && detail && <Detail sc={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function useApply() {
  const lang = useStore((s) => s.lang);
  return (sc: string) => {
    const { sheet, openModal } = useStore.getState();
    if (hasSheet(sheet)) openModal({ kind: "apply", sc }); // replace / add / cancel (偏差 D6)
    else applyBomTo(sc, "replace", lang);
  };
}

export function imageLoadMode(index: number) {
  return index < 8 ? { loading: "eager" as const, fetchPriority: "high" as const } : { loading: "lazy" as const, fetchPriority: "auto" as const };
}

function CardView({ x, index, onOpen }: { x: Card; index: number; onOpen(): void }) {
  const t = useT();
  const m = useMoney(); // history is in USD; shown in the currency picked at the top (JF 2026-10-13)
  const apply = useApply();
  const [broken, setBroken] = useState(false);
  return (
    <div className="card" data-sc={x.sc} onClick={onOpen}>
      {!x.hasF && <span className="wb" title={t("No fabric description in this BOM", "这个 BOM 没有面料描述")}>{t("no fabric", "无面料")}</span>}
      <button className="use" data-use-sc={x.sc} onClick={(e) => { e.stopPropagation(); apply(x.sc); }}>{t("Use BOM", "使用 BOM")}</button>
      <div className="im">{x.img && !broken ? <img {...imageLoadMode(index)} decoding="async" src={x.img} alt="" onError={() => setBroken(true)} /> : <span className="noimg">{t("no picture", "无图")}</span>}</div>
      <div className="bd">
        <div className="sb1">{x.sub}</div>
        <div className="sb2"><span className="mono" style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.style || x.sc}</span><span>{m.fmt(x.fob)}</span></div>
        <div className="sb3">{x.cust}{x.qty ? ` · ${Math.round(x.qty)} pcs` : ""}</div>
        <div className="sb3">{x.desc || x.sc}</div>
      </div>
    </div>
  );
}

/** Detail of one historical style: an overlay in the tab, or in place of the results inside the comparison panel. */
function Detail({ sc, onClose, inline }: { sc: string; onClose(): void; inline?: boolean }) {
  const t = useT();
  const m = useMoney();
  const apply = useApply();
  const lang = useStore((s) => s.lang);
  const [d, setD] = useState<Awaited<ReturnType<typeof api.bom>> | null>(null);
  const [sel, setSel] = useState<Set<number>>(new Set()); // rows picked one by one (JF 2026-10-12)
  useEffect(() => { setSel(new Set()); api.bom(sc).then(setD).catch(onClose); }, [sc]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  if (!d) return null;
  const x = d.item;
  // lines come in cost-sheet order (fabrics, then trims) — the same order and colours as the sheet next to it
  const isFab = (r: (typeof d.lines)[number]) => r.kind === "fabric";
  const toggle = (i: number) => setSel((p) => { const q = new Set(p); if (q.has(i)) q.delete(i); else q.add(i); return q; });
  const allOn = sel.size === d.lines.length && d.lines.length > 0;
  function addPicked() {
    const only = [...sel].sort((a, b) => a - b);
    onClose();
    // an empty sheet gets the style's header as well; otherwise the rows go below what is there
    applyBomTo(sc, hasSheet(useStore.getState().sheet) ? "add" : "replace", lang, only);
  }
  return (
    <DetailWrap inline={inline} onClose={onClose}>
        <button className="ovx" title="Close" onClick={onClose}>×</button>
        <div className="ovtop">
          <div className="ovimg">{x.img ? <img src={bigImg(x.img)} alt="" onError={(e) => { const i = e.currentTarget; if (i.src !== x.img) i.src = x.img; }} /> : <span className="dim">{t("no picture", "无图")}</span>}</div>
          <div>
            <div className="sb1" style={{ color: "var(--accent)", fontWeight: 700, fontSize: 12, letterSpacing: ".06em" }}>{x.cat} · {x.sub}</div>
            <h2 style={{ margin: "4px 0 2px" }}>{x.style || x.sc}</h2><div className="dim">{x.desc}</div>
            <div className="ovsum" style={{ marginTop: 10 }} id="ovCost">
              <div className="tint-fab" id="ovMat"><span>{t("Total material cost (SC)", "总材料成本（SC）")}</span><b>{m.fmt(x.mat)}</b></div>
              <div className="tint-cm"><span>{t("Total CM cost (SC)", "总 CM 成本（SC）")}</span><b>{m.fmt(x.cm)}</b></div>
              <div className="ovfob"><span>{t("Total FOB cost (SC)", "FOB 总成本（SC）")}</span><b>{m.fmt(x.fob)}</b></div>
            </div>
            <p className="dim" style={{ fontSize: 12 }}>{t("Historical reference: SC-level costs and BOM descriptions only. Line unit prices, usages and historical quotes are not provided.", "历史仅提供 SC 总成本和 BOM 文字描述，不提供逐行单价、用量或历史客户报价。")}</p>
            <div className="kv">
              <div>SC</div><div className="mono">{x.sc}</div>
              <div>{t("Customer", "客户")}</div><div>{x.cust}</div>
              <div>{t("Gender", "性别")}</div><div>{x.gender}</div>
              <div>{t("Order qty", "订单量")}</div><div>{x.qty ? Math.round(x.qty) : "—"} pcs</div>
              <div>{t("Factory / country", "工厂 / 国家")}</div><div>{x.fac}{x.co ? " · " + x.co : ""}</div>
              <div>{t("Trade term", "贸易条款")}</div><div><b>{x.tt || "—"}</b></div>
              {/* ex-factory date (JF 2026-10-13): place kept; the developers connect it to the warehouse (IndexItem.exFty) */}
              <div>Ex-fty date</div><div id="ovExFty">{x.exFty || "—"}</div>
              <div>{t("BOM descriptions", "BOM 文字描述")}</div><div>{d.lines.length} {t("lines", "行")}</div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn p" data-use-sc={sc} onClick={() => { onClose(); apply(sc); }}>{t("Use this BOM", "使用这个 BOM")}</button>
              {!inline && hasSheet(useStore.getState().sheet) && <button className="btn" id="cmpThis" onClick={() => {
                useStore.getState().setCompare({ on: true, sc }); onClose(); useStore.getState().go("sheet");
              }}>{t("Compare with the current cost sheet", "和当前 cost sheet 对比")}</button>}
              <button className="btn" onClick={onClose}>{t("Back to results", "返回结果")}</button>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "22px 0 8px", flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, fontSize: 13, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>BOM · {d.lines.length} {t("lines", "行")}</h3>
          <span className="dim" style={{ fontSize: 12 }}>{t("Tick rows to add only those to the cost sheet", "勾选需要的行，只把这些行加到成本表")}</span>
          <button className="btn sm p" id="addPicked" disabled={!sel.size} onClick={addPicked} style={{ marginLeft: "auto" }}>
            {t(`Add ${sel.size} ticked row(s) to the cost sheet`, `把勾选的 ${sel.size} 行加到成本表`)}</button>
        </div>
        <div style={{ overflowX: "auto" }}><table className="t bomt"><tbody>
          <tr><th><input type="checkbox" id="pickAll" checked={allOn} onChange={() => setSel(allOn ? new Set() : new Set(d.lines.map((_, i) => i)))} title={t("All rows", "全选")} /></th>
            <th>{t("Type", "类型")}</th><th>{t("Description", "描述")}</th><th>{t("Position", "部位")}</th><th>{t("Quality # (supplier article)", "Quality #（供应商货号）")}</th>
            <th>{t("Material code", "物料号")}</th><th>{t("Unit", "单位")}</th></tr>
          {d.lines.map((r, i) => (
            <tr key={i} className={(isFab(r) ? "tint-fab" : "tint-trim") + (sel.has(i) ? " picked" : "")} data-bomrow={i} onClick={() => toggle(i)} style={{ cursor: "pointer" }}>
              <td><input type="checkbox" checked={sel.has(i)} onChange={() => toggle(i)} onClick={(e) => e.stopPropagation()} /></td>
              <td style={{ whiteSpace: "nowrap" }}>{isFab(r) ? <b>{t("Fabric", "面料")}</b> : String(r.type || t("Trim", "辅料"))}</td>
              <td>{cleanDesc(r.description)}</td><td>{cleanPos(r.placement)}</td><td className="mono">{r.supplierArticle}</td><td className="mono">{r.code}</td>
              <td>{r.unit}</td>
            </tr>
          ))}
        </tbody></table></div>
    </DetailWrap>
  );
}

function DetailWrap({ inline, onClose, children }: { inline?: boolean; onClose(): void; children: React.ReactNode }) {
  return inline
    ? <div className="ovb inline" id="cmpDetail">{children}</div>
    : <div className="ov on" id="ov" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}><div className="ovb" id="ovb">{children}</div></div>;
}
